/**
 * Resumo de projeção de caixa em 48h (fonte: projection48h da API).
 * Usado no dashboard ERP e no dashboard financeiro.
 */

import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Wallet, TrendingUp } from 'lucide-react'
import './CashFlow48hSummary.css'

const LIQUIDITY_LABELS = {
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa'
}

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value || 0)
}

function formatPeriodLabel(periodFrom, periodTo) {
  if (!periodFrom || !periodTo) return 'próximas 48h'
  const fmt = (iso) => {
    const [y, m, d] = iso.split('-').map(Number)
    return new Date(y, m - 1, d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
  }
  if (periodFrom === periodTo) return fmt(periodFrom)
  return `${fmt(periodFrom)} a ${fmt(periodTo)}`
}

function CashFlow48hSummary({
  data,
  variant = 'dark',
  className = '',
  actionLabel = 'Ver Extrato Conciliado',
  actionPath = '/erp/financeiro/extrato-caixa',
  showCurrentBalance = false
}) {
  const navigate = useNavigate()

  if (!data) {
    return null
  }

  const liquidity = data.liquidity || 'medium'
  const periodLabel = formatPeriodLabel(data.periodFrom, data.periodTo)

  return (
    <div className={`cashflow-48h-summary cashflow-48h-summary--${variant} ${className}`.trim()}>
      <div className="cashflow-48h-summary-header">
        <h3 className="cashflow-48h-summary-title">
          <Wallet size={16} className="cashflow-48h-summary-icon" />
          Projeção 48h
        </h3>
        <div className={`cashflow-48h-summary-badge ${liquidity}`}>
          <TrendingUp size={10} />
          Liquidez {LIQUIDITY_LABELS[liquidity] || 'Média'}
        </div>
      </div>

      <p className="cashflow-48h-summary-period">
        Vencimentos {periodLabel} · saldo após o período
      </p>

      <div className="cashflow-48h-summary-content">
        <div className="cashflow-48h-summary-balance">
          <div>
            <p className="cashflow-48h-summary-balance-label">Saldo após 48h</p>
            <p className="cashflow-48h-summary-balance-value">
              {formatCurrency(data.projectedBalance)}
            </p>
            {showCurrentBalance && (
              <p className="cashflow-48h-summary-current">
                Hoje: {formatCurrency(data.currentBalance)}
              </p>
            )}
          </div>
          <div className="cashflow-48h-summary-net">
            <p className={`cashflow-48h-summary-net-value ${(data.netFlow || 0) >= 0 ? 'positive' : 'negative'}`}>
              {(data.netFlow || 0) >= 0 ? '+' : ''} {formatCurrency(data.netFlow)}
            </p>
            <p className="cashflow-48h-summary-net-label">Fluxo líquido 48h</p>
          </div>
        </div>

        <div className="cashflow-48h-summary-details">
          <div className="cashflow-48h-summary-detail">
            <span>A receber (48h)</span>
            <span className="positive">+ {formatCurrency(data.receivables)}</span>
          </div>
          <div className="cashflow-48h-summary-detail">
            <span>A pagar (48h)</span>
            <span className="negative">- {formatCurrency(data.payables)}</span>
          </div>
        </div>

        {Array.isArray(data.byDay) && data.byDay.length > 0 && variant === 'light' && (
          <div className="cashflow-48h-summary-by-day">
            {data.byDay.map((day) => (
              <div key={day.date} className="cashflow-48h-summary-day-row">
                <span>
                  {new Date(day.date + 'T12:00:00').toLocaleDateString('pt-BR', {
                    weekday: 'short',
                    day: '2-digit',
                    month: '2-digit'
                  })}
                </span>
                <span className={day.netFlow >= 0 ? 'positive' : 'negative'}>
                  {day.netFlow >= 0 ? '+' : ''}{formatCurrency(day.netFlow)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <button
        type="button"
        onClick={() => navigate(actionPath)}
        className="cashflow-48h-summary-btn"
      >
        {actionLabel}
      </button>
    </div>
  )
}

export default CashFlow48hSummary
